import { useEffect } from "react"

// Standalone (iOS Home Screen / installed PWA) recovery guard.
//
// Why this exists: when the app is saved to the iPhone Home Screen and run
// in standalone mode, it runs under a tighter WebKit memory ceiling and has
// no browser chrome to silently reload from. When iOS discards or
// long-suspends the backgrounded WebKit process, the app resumes into a dead
// JS context — frozen, unscrollable, untappable. Regular Safari tabs hide
// this by transparently reloading; standalone apps don't. This module
// detects the two resume paths that land on a stale context and reloads to a
// clean one.
//
// IMPORTANT: this module touches window/document/navigator only inside
// functions, never at top level, so it stays importable in Node during SSR
// (editor pages pull in `useBlockAutoReload` below). `installStandaloneRecovery`
// is called ONLY from the client Inertia entrypoint, never from the SSR
// entrypoint.

// Reload only if the app was backgrounded for longer than this. Short
// app-switches (checking a notification, copying a link) resume fine; it's
// the long suspends where iOS reclaims the WebKit process that strand us.
const HIDDEN_RELOAD_THRESHOLD_MS = 30 * 60 * 1000 // ~30 min

// True when running as an installed/standalone app rather than a Safari tab.
// `display-mode: standalone` covers installed PWAs; `navigator.standalone`
// is the legacy iOS Home Screen signal.
function isStandalone(): boolean {
  if (typeof window === "undefined") return false
  const byDisplayMode = window.matchMedia?.("(display-mode: standalone)").matches ?? false
  const byNavigator =
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  return byDisplayMode || byNavigator
}

// --- Unsaved-work opt-out (reference counted) --------------------------
//
// The recovery guard must NEVER reload while an editor holds unsaved edits —
// a reload would destroy user-typed work. Editors register a block while
// dirty and release it once the save lands. Ref-counted (not a single
// boolean) so multiple dirty editors compose, and an unmount releasing its
// own reference can't strand a block another editor still holds.
let blockCount = 0

// Acquire a block. Returns a release function; calling it more than once is
// a no-op, so callers can wire it straight into React effect cleanup.
export function blockAutoReload(): () => void {
  blockCount += 1
  let released = false
  return () => {
    if (released) return
    released = true
    blockCount = Math.max(0, blockCount - 1)
  }
}

// Whether an automatic recovery reload is currently suppressed.
function isAutoReloadBlocked(): boolean {
  if (blockCount > 0) return true
  // Escape hatch for non-React surfaces (imperative editors, third-party
  // widgets) that can't hold a React-scoped block.
  if (
    typeof window !== "undefined" &&
    (window as Window & { __sparkdropBlockAutoReload?: boolean }).__sparkdropBlockAutoReload
  ) {
    return true
  }
  return false
}

// React hook: hold a block reference while `active` is true; release it when
// `active` flips false or the component unmounts. Set `active` true from the
// first unsaved edit until the save lands.
export function useBlockAutoReload(active: boolean): void {
  useEffect(() => {
    if (!active) return
    return blockAutoReload()
  }, [active])
}

// Install the recovery guard. No-op unless running standalone. Safe to call
// once from the client entrypoint; touches the DOM only past the standalone
// check.
export function installStandaloneRecovery(): void {
  if (typeof window === "undefined") return
  if (!isStandalone()) return

  // (a) bfcache restore. A standalone app resumed from the back/forward
  // cache returns to a snapshot of a possibly-stale JS context; reload for a
  // clean one (unless an editor is mid-edit).
  window.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted && !isAutoReloadBlocked()) {
      window.location.reload()
    }
  })

  // (b) Long suspend. iOS may freeze or discard the backgrounded WebKit
  // process. Stamp when we go hidden; on resume, if we were backgrounded
  // past the threshold, reload rather than risk operating in a dead context.
  let hiddenAt: number | null = null
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now()
      return
    }
    if (document.visibilityState === "visible") {
      const backgroundedFor = hiddenAt == null ? 0 : Date.now() - hiddenAt
      hiddenAt = null
      if (backgroundedFor > HIDDEN_RELOAD_THRESHOLD_MS && !isAutoReloadBlocked()) {
        window.location.reload()
      }
    }
  })
}
